'use client';

import { useRef, useState } from 'react';
import { usePathname } from 'next/navigation';

const WA_URL = 'https://wa.me/919709723107';
const SIZE = 48; // px

/**
 * Floating WhatsApp button:
 * - draggable (movable) anywhere on screen
 * - dismissable (×) — hides for the current page
 * - reappears when the route changes ("opens again on another page")
 */
export default function WhatsAppFloat() {
  const pathname = usePathname();
  const [visible, setVisible] = useState(true);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null); // null = default corner
  const drag = useRef<{ sx: number; sy: number; ox: number; oy: number; moved: boolean } | null>(null);

  // Reappear on every navigation — adjust during render when the route changes
  const [prevPathname, setPrevPathname] = useState(pathname);
  if (pathname !== prevPathname) {
    setPrevPathname(pathname);
    setVisible(true);
  }

  if (!visible) return null;

  const onPointerDown = (e: React.PointerEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { sx: e.clientX, sy: e.clientY, ox: rect.left, oy: rect.top, moved: false };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.sx;
    const dy = e.clientY - drag.current.sy;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) drag.current.moved = true;
    const x = Math.max(4, Math.min(window.innerWidth - SIZE - 4, drag.current.ox + dx));
    const y = Math.max(4, Math.min(window.innerHeight - SIZE - 4, drag.current.oy + dy));
    setPos({ x, y });
  };

  const onPointerUp = () => {
    const wasMoved = drag.current?.moved;
    drag.current = null;
    // A tap (no real movement) opens WhatsApp; a drag just repositions.
    if (!wasMoved) window.open(WA_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className={`fixed z-[60] ${pos ? '' : 'bottom-5 right-5'}`}
      style={pos ? { left: pos.x, top: pos.y } : undefined}
    >
      <div className="relative group">
        {/* Dismiss — only visible on hover to avoid clutter */}
        <button
          type="button"
          onClick={() => setVisible(false)}
          aria-label="Hide WhatsApp button"
          className="absolute -top-1 -right-1 z-10 w-4 h-4 rounded-full bg-zinc-700/90 text-white text-[10px] leading-none flex items-center justify-center shadow opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity hover:bg-zinc-900"
        >
          ×
        </button>

        {/* Draggable WhatsApp button */}
        <button
          type="button"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          aria-label="Chat with us on WhatsApp"
          title="Chat with us on WhatsApp (drag to move)"
          className="w-12 h-12 rounded-full bg-green-500 hover:bg-green-600 text-white shadow-md hover:shadow-lg flex items-center justify-center touch-none cursor-grab active:cursor-grabbing transition-colors"
          style={{ touchAction: 'none' }}
        >
          <svg className="w-6 h-6 pointer-events-none" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.786"/>
          </svg>
        </button>
      </div>
    </div>
  );
}
